// ============================================================================
// Universal Turnover Task Modal (Create + Edit) — Slice 3 simple sheet
// ============================================================================

import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Clock, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTurnoverTaskModal } from "@/contexts/TurnoverTaskModalContext";
import { useTurnoverTaskForm } from "@/hooks/useTurnoverTaskForm";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import {
  canDeleteTasks,
  type PropertyAccessLevel,
  type PropertyUserRole,
} from "@/lib/propertyTabs";
import { resolveTeamPermissions } from "@/lib/teamPermissions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useReservations } from "@/hooks/useReservations";
import { chipClassName, resolveTurnoverWindow } from "@/lib/turnoverWindow";
import { OwnerGroupSentences } from "./OwnerGroupSentences";

/** 30-min slots from 00:00 to 23:30 */
const TIME_SLOTS = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${h.toString().padStart(2, "0")}:00`);
    out.push(`${h.toString().padStart(2, "0")}:30`);
  }
  return out;
})();

function formatSlotLabel(slot: string): string {
  const [hs, ms] = slot.split(":").map(Number);
  const d = new Date();
  d.setHours(hs, ms, 0, 0);
  return format(d, "h:mm a").replace(":00", "").replace(" ", "").toLowerCase();
}

function initialOf(name: string): string {
  const t = name.trim();
  return t ? t[0]!.toUpperCase() : "";
}

export function TurnoverTaskModal() {
  const isMobile = useIsMobile();
  const { user, profile } = useAuth();
  const ctx = usePropertyContext();
  const { state, closeTurnoverTaskModal } = useTurnoverTaskModal();
  const { open, mode, propertyId, taskId, reservationId, initialDate } = state;

  const form = useTurnoverTaskForm({
    mode,
    propertyId: open && propertyId ? propertyId : "",
    taskId,
    reservationId,
    initialDate,
  });
  const { data: reservations = [] } = useReservations(open ? propertyId : undefined);

  // Modal mounts at App root (outside PropertyContextProvider), so resolve access
  // for this propertyId via RPC when context is missing or for a different property.
  const ctxMatchesProperty =
    !!propertyId && ctx?.propertyId === propertyId && ctx.role != null;
  const { data: modalAccess } = useQuery({
    queryKey: ["my-property-access-level", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;
      const { data, error } = await supabase.rpc("get_my_property_access_level", {
        p_property_id: propertyId,
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return row as { access_level: string; is_owner: boolean; permissions?: unknown };
    },
    enabled:
      open &&
      !!propertyId &&
      !!user?.id &&
      profile?.role !== "cleaner" &&
      !ctxMatchesProperty,
  });

  const canDelete = useMemo(() => {
    if (ctxMatchesProperty) {
      return (
        ctx!.isOwner ||
        canDeleteTasks(ctx!.role!, ctx!.accessLevel, ctx!.permissions)
      );
    }
    const ownerFromProperty = !!(
      form.property &&
      user?.id &&
      (form.property as { owner_id?: string }).owner_id === user.id
    );
    if (ownerFromProperty || modalAccess?.is_owner === true) return true;
    if (!modalAccess) return false;
    const accessLevel: PropertyAccessLevel | null =
      modalAccess.access_level === "full" || modalAccess.access_level === "limited"
        ? modalAccess.access_level
        : null;
    const role: PropertyUserRole = "cohost";
    const permissions = resolveTeamPermissions(
      modalAccess.permissions ?? null,
      accessLevel
    );
    return canDeleteTasks(role, accessLevel, permissions);
  }, [
    ctx,
    ctxMatchesProperty,
    form.property,
    modalAccess,
    user?.id,
  ]);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passAlongOpen, setPassAlongOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  if (!open || !propertyId) return null;

  const setCleaner = (value: string) => {
    form.setAssigneeValue(value);
    if ((form.checklistAssignments ?? []).length > 0) {
      form.updateChecklistAssignment(0, { assigneeValue: value });
    }
  };

  const handleSave = async (opts?: { confirmPassAlong?: boolean }) => {
    if (
      mode === "edit" &&
      form.isStartedTask &&
      form.assigneeChangedFromSaved &&
      !opts?.confirmPassAlong
    ) {
      setPassAlongOpen(true);
      return;
    }
    const wasPassAlong = Boolean(
      mode === "edit" && form.isStartedTask && form.assigneeChangedFromSaved
    );
    try {
      await form.save();
      setSavedFlash(true);
      setPassAlongOpen(false);
      toast({
        title: wasPassAlong ? "Passed along" : "Saved",
        description: wasPassAlong
          ? "Progress stayed on this job. The new person can continue where it left off."
          : undefined,
      });
      closeTurnoverTaskModal();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      toast({
        title: "Only Full Access can delete tasks",
        description: "Ask the account owner if a turnover needs to be removed.",
        variant: "destructive",
      });
      return;
    }
    try {
      await form.remove();
      toast({
        title: "Removed",
        description: "Deleted from your schedule. Sync will not bring it back.",
      });
      setDeleteDialogOpen(false);
      closeTurnoverTaskModal();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete task";
      toast({
        title: "Error",
        description: msg,
        variant: "destructive",
      });
    }
  };

  const isOwner = !!(form.property && user?.id && (form.property as { owner_id?: string }).owner_id === user.id);

  type Person = { value: string; name: string; role: string; initial: string; avatarUrl?: string | null };
  const people: Person[] = [];
  for (const m of form.cleanerMembers ?? []) {
    if (!m?.value) continue;
    people.push({
      value: m.value,
      name: m.name?.trim() || m.email?.split("@")[0] || "Cleaner",
      role: "Cleaner",
      initial: initialOf(m.name || m.email || "C"),
      avatarUrl: m.avatarUrl ?? null,
    });
  }
  for (const m of form.teamMembers ?? []) {
    if (!m?.value) continue;
    people.push({
      value: m.value,
      name: m.name?.trim() || m.email?.split("@")[0] || "Team",
      role: "Team",
      initial: initialOf(m.name || m.email || "T"),
      avatarUrl: m.avatarUrl ?? null,
    });
  }
  people.push({
    value: "unassigned",
    name: "Unassigned",
    role: "Leave open",
    initial: "",
  });
  // Account owner: self-assign at bottom so they can start the task themselves.
  if (isOwner && user?.id) {
    const selfName =
      profile?.full_name?.trim() ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
      "me";
    people.push({
      value: "self",
      name: "Assign to me",
      role: "Owner",
      initial: initialOf(selfName),
      avatarUrl: profile?.avatar_url?.trim() || null,
    });
  }

  const pick = form.assigneeValue || "unassigned";
  // Slice 12: calendar turnovers = assign only (no date/time pickers).
  const assignOnly = mode === "edit" || Boolean(reservationId) || Boolean(form.task?.reservation_id);
  const canSave = assignOnly ? !form.isSaving : !!form.scheduledDate && !form.isSaving;

  const checkoutDate =
    (form.reservationData?.end_date as string | undefined) ||
    (form.scheduledDate ? format(form.scheduledDate, "yyyy-MM-dd") : "") ||
    (form.task?.scheduled_date as string | undefined) ||
    "";
  const windowInfo = checkoutDate
    ? resolveTurnoverWindow({
        checkoutDate,
        reservations: (reservations || []).map((r) => ({
          id: r.id,
          start_date: r.start_date,
          end_date: r.end_date,
          guest_name: (r as { guest_name?: string | null }).guest_name ?? null,
          guest_count: (r as { guest_count?: number | null }).guest_count ?? null,
          guest_photo_url: (r as { guest_photo_url?: string | null }).guest_photo_url ?? null,
          check_in_time: (r as { check_in_time?: string | null }).check_in_time ?? null,
          check_out_time: (r as { check_out_time?: string | null }).check_out_time ?? null,
          early_checkout_detected: (r as { early_checkout_detected?: boolean | null }).early_checkout_detected ?? null,
          reservation_type: (r as { reservation_type?: string | null }).reservation_type ?? null,
        })),
        departingReservationId: form.task?.reservation_id || reservationId || null,
        taskStatus: form.task?.status,
      })
    : null;

  const sheetTitle = "Assign";

  const formContent = (
    <div className="flex flex-col">
      {savedFlash && (
        <div className="mx-6 mt-4 flex items-center gap-2.5 rounded-[14px] border-[1.5px] border-[#bfe3de] bg-[#eaf6f4] px-[18px] py-3 text-[15px] font-bold text-[#15776d]">
          Saved
        </div>
      )}

      {form.isStartedTask ? (
        <div className="mx-6 mt-4 rounded-[14px] border-[1.5px] border-[#fde68a] bg-[#fffbeb] px-[18px] py-3 text-[14px] font-medium text-[#92400e]">
          This job is already in progress. Changing who it’s assigned to passes the same progress to them.
        </div>
      ) : null}

      {assignOnly && windowInfo ? (
        <div className="flex flex-col gap-2 border-b-[1.5px] border-[#e6edf1] px-6 py-6">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-bold tracking-[0.3px]",
                chipClassName(windowInfo.chip),
              )}
            >
              {windowInfo.chip}
            </span>
          </div>
          <div className="text-[24px] font-bold tracking-[-0.5px] text-[#0f1720]">{windowInfo.label}</div>
          <OwnerGroupSentences
            outSentence={windowInfo.outSentence}
            inSentence={windowInfo.inSentence}
            size="sheet"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3.5 px-6 pb-1 pt-[22px]">
          <div className="flex flex-col gap-2">
            <div className="text-xs font-bold tracking-[0.3px] text-[#64748b]">Date</div>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex h-[54px] w-full items-center justify-between rounded-[13px] border-[1.5px] border-[#e6edf1] px-4 text-left text-base font-medium hover:border-[#24a89a]",
                    !form.scheduledDate && "text-[#94a3b8]",
                  )}
                >
                  {form.scheduledDate ? format(form.scheduledDate, "EEE, MMM d") : "Pick date"}
                  <CalendarIcon className="h-[17px] w-[17px] text-[#94a3b8]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.scheduledDate}
                  onSelect={(date) => {
                    form.setScheduledDate(date);
                    setDatePickerOpen(false);
                  }}
                  disabled={form.isDateDisabled}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-xs font-bold tracking-[0.3px] text-[#64748b]">Time</div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-[54px] w-full items-center justify-between rounded-[13px] border-[1.5px] border-[#e6edf1] px-4 text-left text-base font-medium hover:border-[#24a89a]"
                >
                  <span>
                    {formatSlotLabel(form.startTime)} – {formatSlotLabel(form.endTime)}
                  </span>
                  <Clock className="h-[17px] w-[17px] text-[#94a3b8]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 space-y-3 p-3" align="end">
                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-[#64748b]">Start</div>
                  <Select value={form.startTime} onValueChange={form.setStartTime}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50 max-h-[200px]">
                      {TIME_SLOTS.map((slot) => (
                        <SelectItem key={`s-${slot}`} value={slot}>
                          {formatSlotLabel(slot)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-[#64748b]">End</div>
                  <Select value={form.endTime} onValueChange={form.setEndTime}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50 max-h-[200px]">
                      {TIME_SLOTS.map((slot) => (
                        <SelectItem key={`e-${slot}`} value={slot}>
                          {formatSlotLabel(slot)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 px-6 pb-2 pt-[18px]">
        <div className="text-xs font-bold tracking-[0.3px] text-[#64748b]">Assign to</div>
        <div className="flex flex-col gap-2.5">
          {people.map((c) => {
            const selected = pick === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => setCleaner(c.value)}
                className={cn(
                  "flex items-center gap-3.5 rounded-[14px] border-[1.5px] px-4 py-3 text-left transition-colors",
                  selected
                    ? "border-[#24a89a] bg-[#eaf6f4]"
                    : "border-[#e6edf1] bg-white hover:border-[#24a89a]",
                )}
              >
                <div
                  className={cn(
                    "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-white",
                    selected ? "border-[#24a89a]" : "border-[#d7e0e6]",
                  )}
                >
                  {selected && <div className="h-2.5 w-2.5 rounded-full bg-[#24a89a]" />}
                </div>
                <div
                  className={cn(
                    "flex h-[34px] w-[34px] shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold",
                    c.avatarUrl
                      ? "bg-[#eef3f6]"
                      : c.initial
                        ? "bg-[#e2eeed] text-[#15776d]"
                        : "bg-[#eef3f6] text-[#94a3b8]",
                  )}
                >
                  {c.avatarUrl ? (
                    <img
                      src={c.avatarUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    c.initial || "–"
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-[#0f1720]">{c.name}</div>
                  <div className="text-[13px] font-medium text-[#64748b]">{c.role}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-6 py-[18px]">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            "h-[54px] rounded-[14px] px-[34px] text-[17px] font-bold text-white shadow-none",
            canSave ? "bg-[#24a89a] hover:bg-[#1f958a]" : "bg-[#cfdcdf] hover:bg-[#cfdcdf]",
          )}
        >
          {form.isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
        {mode === "edit" && canDelete && (
          <button
            type="button"
            onClick={() => setDeleteDialogOpen(true)}
            className="text-sm font-medium text-[#94a3b8] hover:text-[#b4453a]"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );

  const deleteDialog = (
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove from schedule?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the turnover and its reservation from your calendar. Sync will not bring it back.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {form.isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const passAlongDialog = (
    <AlertDialog open={passAlongOpen} onOpenChange={setPassAlongOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Pass along this in-progress job?</AlertDialogTitle>
          <AlertDialogDescription>
            This turnover has already started. Progress stays saved — the new person continues where it left off.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => void handleSave({ confirmPassAlong: true })}
            className="bg-[#24a89a] text-white hover:bg-[#1f958a]"
          >
            {form.isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Pass along
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const header = (
    <div className="flex items-center justify-between border-b-[1.5px] border-[#e6edf1] px-6 py-5">
      <div className="flex flex-col gap-0.5">
        <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">{sheetTitle}</div>
      </div>
      <button
        type="button"
        onClick={() => closeTurnoverTaskModal()}
        className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] border-[1.5px] border-[#e6edf1] hover:bg-[#f4f7f9]"
        aria-label="Close"
      >
        <X className="h-4 w-4 text-[#64748b]" strokeWidth={2.4} />
      </button>
    </div>
  );

  if (form.isLoading) {
    return (
      <Dialog open={open} onOpenChange={() => closeTurnoverTaskModal()}>
        <DialogContent className="sm:max-w-[460px]">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={(o) => !o && closeTurnoverTaskModal()}>
          <DrawerContent className="max-h-[90vh] flex flex-col overflow-hidden rounded-t-[20px]">
            <DrawerHeader className="sr-only">
              <DrawerTitle>{sheetTitle}</DrawerTitle>
              <DrawerDescription>Turnover</DrawerDescription>
            </DrawerHeader>
            {header}
            <div className="overflow-y-auto flex-1 min-h-0">{formContent}</div>
          </DrawerContent>
        </Drawer>
        {deleteDialog}
        {passAlongDialog}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && closeTurnoverTaskModal()}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[460px] max-h-[85vh] flex flex-col [&>button]:hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{sheetTitle}</DialogTitle>
            <DialogDescription>Turnover</DialogDescription>
          </DialogHeader>
          {header}
          <div className="overflow-y-auto flex-1 min-h-0">{formContent}</div>
        </DialogContent>
      </Dialog>
      {deleteDialog}
      {passAlongDialog}
    </>
  );
}
